import { translateUi } from "@/shared/lib/ui-translate";
import { handleError } from "@/shared/api/error-handler";
import type { UserProfile, RiotAccount } from "@/shared/api/user";
import { AUTH_CONSTANTS, API_STATUS_CODES } from "@/shared/config";

import { api } from "./api";
import { getProfileIconUrl } from "./champion-image";

/** KAN-208: настройки приватности профиля (форма ответа /me и payload /me/update). */
export interface UserPrivacySettings {
  showStats?: boolean;
  showOnlineStatus?: boolean;
  allowMessages?: boolean;
  showInSearch?: boolean;
}

/** KAN-208: настройки уведомлений профиля. */
export interface UserNotificationSettings {
  email?: boolean;
  tournamentStart?: boolean;
  newsAndRecommendations?: boolean;
  /** KAN-1362: категория писем «Команды». */
  emailTeams?: boolean;
}

export interface LoginRequestDto {
  username: string;
  password: string;
}

export interface RegisterRequestDto {
  username: string;
  password: string;
  displayName?: string;
}

export interface AuthResponseDto {
  isSuccess: boolean;
  token: string;
  refreshToken: string;
  expiresIn: number;
  user?: UserProfile;
  error?: string;
  emailVerificationRequired?: boolean;
  maskedEmail?: string;
  /** Код верный, но аккаунта с этим адресом нет — нужен шаг создания аккаунта. Токенов нет. */
  registrationRequired?: boolean;
  /** Адрес, к которому относится ответ; приходит вместе с registrationRequired. */
  email?: string;
}

export interface OAuthUrlRequest {
  provider: string;
  redirectUri: string;
  state?: string;
  /** KAN-1536: относительный путь сайта, вернётся в ответе auth/oauth/callback. */
  returnPath?: string;
}

export interface OAuthCallbackRequest {
  provider: string;
  code: string;
  redirectUri: string;
  state?: string;
}

export interface TokenResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export class AuthService {
  // Вход по логину и паролю
  /**
   * Ошибку НЕ разбираем и НЕ тостим здесь — пробрасываем как есть (KAN-1216).
   *
   * Раньше catch превращал ответ сервера в `{ isSuccess: false, error }`, а useLogin
   * бросал из этого голый `Error`. Форма разбирает ошибку по `response.data.code`
   * (`extractAuthError`) и получала пустоту: неверный пароль, «email не
   * подтверждён» и лимит попыток (429) одинаково показывались как «Ошибка
   * соединения». Плюс `handleError` отсюда давал тост «токен сессии
   * недействителен» — для /auth/login 401 значит неверный пароль, а не сессию.
   */
  static async login(credentials: LoginRequestDto): Promise<AuthResponseDto> {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  }

  // Тестовый вход для сторонних разработчиков (без пароля)
  static async testLogin(): Promise<AuthResponseDto> {
    try {
      const response = await api.post("/auth/test-login");

      return response.data;
    } catch (error: unknown) {
      handleError(error, { component: "AuthService", action: "testLogin" });

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { status: number; data?: { error?: string } };
        };
        if (axiosError.response) {
          return {
            isSuccess: false,
            token: "",
            refreshToken: "",
            expiresIn: 0,
            error:
              axiosError.response.data?.error ||
              translateUi("litAuthService.oshibkaPriTestovomVhode"),
          };
        }
      }

      return {
        isSuccess: false,
        token: "",
        refreshToken: "",
        expiresIn: 0,
        error: translateUi("litAuthService.oshibkaSoedineniya"),
      };
    }
  }

  // Регистрация нового пользователя
  static async register(
    userData: RegisterRequestDto,
  ): Promise<AuthResponseDto> {
    try {
      // ИСПРАВЛЕНО: Используем только один API клиент - axios
      const response = await api.post("/auth/register", userData);

      return response.data;
    } catch (error: unknown) {
      handleError(error, { component: "AuthService", action: "register" });
      return {
        isSuccess: false,
        token: "",
        refreshToken: "",
        expiresIn: 0,
        error: translateUi("litAuthService.oshibkaPriRegistratsii"),
      };
    }
  }

  // Выход
  static logout(): void {
    // best-effort; UI состояние сбрасывается выше (zustand)
    api.post("/auth/logout").catch(() => { });
  }


  // Проверка аутентификации
  static isAuthenticated(): boolean {
    return typeof window !== "undefined";
  }

  // Получение URL для OAuth авторизации
  static async getOAuthUrl(request: OAuthUrlRequest): Promise<string> {
    try {
      const response = await api.post("/auth/oauth/url", request);
      return response.data.url;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "getOAuthUrl",
        meta: { provider: request.provider },
      });
      throw error;
    }
  }

  // Обработка OAuth callback
  // Бэкенд ставит sid-cookie и отвечает { success, isNewUser, returnPath }, токена в теле нет.
  static async handleOAuthCallback(
    request: OAuthCallbackRequest,
  ): Promise<{ success?: boolean; isNewUser?: boolean; returnPath?: string | null }> {
    try {

      const response = await api.post("/auth/oauth/callback", request, {
        timeout: AUTH_CONSTANTS.OAUTH_CALLBACK_TIMEOUT_MS,
      });

      return response.data;
    } catch (error) {
      console.error(`Ошибка OAuth callback для ${request.provider}:`, error);
      handleError(error, {
        component: "AuthService",
        action: "oauthCallback",
        meta: { provider: request.provider },
      });
      throw error;
    }
  }

  // Получение данных текущего пользователя
  static async getCurrentUser(): Promise<UserProfile> {
    try {

      // КРИТИЧНО: /me должен вызываться ТОЛЬКО из браузера, не из SSR
      // Иначе Next.js делает серверный запрос и теряет Authorization header
      if (typeof window === "undefined") {
        console.warn(
          "[AuthService] getCurrentUser вызван на сервере (SSR), пропускаем",
        );
        throw new Error("getCurrentUser can only be called from the browser");
      }

      const response = await api.get("/me");

      if (!response.data || (!response.data.id && !response.data.username)) {
        console.error(
          "[AuthService] Некорректные данные пользователя:",
          response.data,
        );
        throw new Error("Некорректные данные пользователя");
      }

      // Приводим данные к типу UserProfile из zod-схемы
      const userData: UserProfile = {
        id: response.data.id,
        username: response.data.username,
        displayName: response.data.displayName,
        isVerified: response.data.isVerified || false,
        platformRoles: Array.isArray(response.data.platformRoles)
          ? response.data.platformRoles
          : Array.isArray(response.data.PlatformRoles)
            ? response.data.PlatformRoles
            : [],
        primaryPlatformRole:
          response.data.primaryPlatformRole ??
          response.data.PrimaryPlatformRole ??
          "User",
        isOnline: response.data.isOnline || false,
        status: response.data.status || "offline",
        avatar: response.data.avatar ?? response.data.avatarUrl ?? "",
        banner: response.data.banner ?? "",
        about: response.data.about ?? "",
        location: response.data.location ?? "",
        languages: response.data.languages || [],
        website: response.data.website ?? "",
        isAdmin:
          typeof response.data.isAdmin === "boolean"
            ? response.data.isAdmin
            : (response.data.primaryPlatformRole ??
              response.data.PrimaryPlatformRole ??
              "User") === "Moder" ||
            (response.data.primaryPlatformRole ??
              response.data.PrimaryPlatformRole ??
              "User") === "SiteAdmin",
        isSiteAdmin:
          typeof response.data.isSiteAdmin === "boolean"
            ? response.data.isSiteAdmin
            : (response.data.primaryPlatformRole ??
              response.data.PrimaryPlatformRole ??
              "User") === "SiteAdmin",
        socialLinks: response.data.socialLinks || {},
        stats: response.data.stats || {
          totalTournaments: 0,
          wins: 0,
          winrate: 0,
          kda: "0/0/0",
          rank: 0,
        },
        privacy: response.data.privacy || {
          showStats: true,
          showOnlineStatus: true,
          allowMessages: true,
          showInSearch: true,
        },
        notifications: response.data.notifications || {
          email: true,
          tournamentStart: true,
          newsAndRecommendations: false,
          emailTeams: true,
        },
        achievements: response.data.achievements || [],
        favoriteChampions: response.data.favoriteChampions || [],
        championMasteries: response.data.championMasteries || [],
        teams: response.data.teams || [],
        riotAccounts: response.data.riotAccounts || [],
        streams: response.data.streams || [],
        hasPro: Boolean(
          response.data.hasPro ?? response.data.HasPro ?? false,
        ),
        proNicknameStyle:
          (response.data.proNicknameStyle ??
            response.data.ProNicknameStyle ??
            0) as number,
        proNicknameColorA:
          (response.data.proNicknameColorA ??
            response.data.ProNicknameColorA ??
            null) as string | null,
        proNicknameColorB:
          (response.data.proNicknameColorB ??
            response.data.ProNicknameColorB ??
            null) as string | null,
      };

      return userData;
    } catch (error: unknown) {
      console.error(
        "[AuthService] Ошибка при получении текущего пользователя:",
        error,
      );
      handleError(error, { component: "AuthService", action: "getMe" });

      // Если ошибка 401, значит токен недействителен
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as { response?: { status: number } };
        if (axiosError.response?.status === API_STATUS_CODES.UNAUTHORIZED) {
          // cookie-session: просто считаем пользователя неаутентифицированным
        }
      }

      throw error;
    }
  }

  // Обновление данных пользователя
  static async updateUserProfile(userData: {
    displayName?: string;
    about?: string | null;
    location?: string | null;
    website?: string | null;
    avatarUrl?: string | null;
    banner?: string | null;
    languages?: string[];
    status?: string;
    socialLinks?: {
      x?: string;
      discord?: string;
      youtube?: string;
      telegram?: string;
      twitch?: string;
      steam?: string;
      vk?: string;
    };
    privacy?: UserPrivacySettings;
    notifications?: UserNotificationSettings;
    proNicknameStyle?: number;
    proNicknameColorA?: string | null;
    proNicknameColorB?: string | null;
  }): Promise<UserProfile> {
    try {
      const response = await api.put("/me/update", userData);
      const result = response.data.user || response.data;
      return result;
    } catch (error) {
      handleError(error, { component: "AuthService", action: "updateUser" });
      throw error;
    }
  }

  // Привязка OAuth аккаунта
  static async linkOAuthAccount(
    provider: string,
    code: string,
    redirectUri: string,
  ): Promise<boolean> {
    try {
      const response = await api.post("/auth/oauth/link", {
        provider,
        code,
        redirectUri,
      });
      return response.data.success;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "linkAccount",
        provider,
      });
      throw error;
    }
  }

  // Отвязка OAuth аккаунта
  static async unlinkOAuthAccount(provider: string): Promise<boolean> {
    try {
      const response = await api.delete(`/auth/oauth/unlink/${provider}`);
      return response.data.success;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "unlinkAccount",
        provider,
      });
      throw error;
    }
  }

  // Получение списка привязанных OAuth аккаунтов
  static async getLinkedAccounts() {
    try {
      const response = await api.get("/auth/oauth/accounts");
      return response.data;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "getLinkedAccounts",
      });
      throw error;
    }
  }

  // Добавление Riot аккаунта (игровая роль живёт на профиле, не на аккаунте)
  static async addRiotAccount(accountData: {
    gameName: string;
    tagLine: string;
    region: string;
    isPrimary: boolean;
    verificationIcon: number;
  }) {
    try {
      const response = await api.post("/riot-accounts", {
        gameName: accountData.gameName,
        tagLine: accountData.tagLine,
        region: accountData.region,
        isPrimary: accountData.isPrimary,
        verificationIcon: accountData.verificationIcon,
      });

      const backendAccount = response.data;
      return {
        id: backendAccount.id,
        gameName: backendAccount.gameName,
        tagLine: backendAccount.tagLine,
        region: backendAccount.region,
        verified: backendAccount.verified,
        avatar: getProfileIconUrl(backendAccount.profileIconId),
        profileIconId: backendAccount.profileIconId,
        isMain: backendAccount.isPrimary,
        rank: backendAccount.currentRank || "Без ранга",
      };
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "addRiotAccount",
      });
      throw error;
    }
  }

  // Удаление Riot аккаунта
  static async deleteRiotAccount(accountId: string) {
    try {
      const response = await api.delete(`/riot-accounts/${accountId}`);
      return response.data;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "deleteRiotAccount",
      });
      throw error;
    }
  }

  // Получение Riot аккаунтов пользователя
  static async getRiotAccounts(): Promise<RiotAccount[]> {
    try {
      const response = await api.get<RiotAccount[]>("/riot-accounts");
      return response.data;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "getRiotAccounts",
      });
      throw error;
    }
  }

  // Верификация Riot аккаунта через иконку профиля
  static async verifyRiotAccount(data: {
    gameName: string;
    tagLine: string;
    region: string;
    verificationIcon: number;
  }): Promise<{ isIconValid: boolean; message?: string }> {
    try {
      const response = await api.post("/riot-accounts/verify-icon", data);
      return response.data;
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "verifyRiotAccount",
      });
      throw error;
    }
  }

  // Резолв Riot ID до верификации: существует ли аккаунт и на каком регионе.
  // region === null → аккаунт есть, но регион не определился (фолбэк на ручной выбор).
  // Ошибку НЕ глушим — вызывающий (модалка) сам решает: not-found блокирует,
  // сетевой сбой → graceful degrade с ручным регионом.
  static async resolveRiotId(
    gameName: string,
    tagLine: string,
  ): Promise<{
    exists: boolean;
    puuid?: string;
    gameName?: string;
    tagLine?: string;
    region?: string | null;
  }> {
    const response = await api.post("/riot-accounts/resolve", {
      gameName,
      tagLine,
    });
    return response.data;
  }

  static async setPrimaryRiotAccount(accountId: string): Promise<void> {
    try {
      await api.post(`/riot-accounts/${accountId}/set-primary`);
    } catch (error) {
      handleError(error, {
        component: "AuthService",
        action: "setPrimaryRiotAccount",
      });
      throw error;
    }
  }

  // Регистрация по email + username + пароль
  static async registerWithEmail(data: {
    email: string;
    username: string;
    password: string;
    displayName?: string;
    /** Согласие на обработку персональных данных (152-ФЗ). Обязательно. */
    pdnConsent: boolean;
    /** KAN-1362: «Новости GoodLuck» — только явная галочка. */
    newsOptIn?: boolean;
    /** KAN-1103: токен Cloudflare Turnstile. Пустой, если капча выключена в сборке. */
    turnstileToken?: string;
  }): Promise<AuthResponseDto> {
    const response = await api.post("/auth/register-email", data);
    return response.data;
  }

  // Отправить код на email
  static async sendEmailCode(
    email: string,
    purpose: "email_verification" | "passwordless_login" | "password_reset",
    /** KAN-1103: токен Cloudflare Turnstile. */
    turnstileToken?: string,
  ): Promise<{ message: string }> {
    const response = await api.post("/auth/email-code/send", { email, purpose, turnstileToken });
    return response.data;
  }

  // Верифицировать email кодом
  static async verifyEmail(
    email: string,
    code: string,
  ): Promise<AuthResponseDto> {
    const response = await api.post("/auth/email-code/verify", { email, code });
    return response.data;
  }

  // Вход по email + код
  static async loginWithEmailCode(
    email: string,
    code: string,
  ): Promise<AuthResponseDto> {
    const response = await api.post("/auth/email-code/login", { email, code });
    return response.data;
  }

  // Создание аккаунта после подтверждённого кода: пароль не нужен, владение
  // почтой уже доказано. Код передаём тот же, что вводили на шаге входа.
  static async completeEmailSignup(payload: {
    email: string;
    code: string;
    username: string;
    displayName?: string;
    pdnConsent: boolean;
    newsOptIn?: boolean;
  }): Promise<AuthResponseDto> {
    const response = await api.post("/auth/email-code/signup", payload);
    return response.data;
  }

  // Установить пароль (для OAuth-юзеров без пароля)
  static async setPassword(newPassword: string): Promise<void> {
    await api.post("/auth/password/set", { newPassword });
  }

  // Сменить пароль
  static async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await api.post("/auth/password/change", { currentPassword, newPassword });
  }

  // Сбросить пароль
  static async resetPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<AuthResponseDto> {
    const response = await api.post("/auth/password/reset", {
      email,
      code,
      newPassword,
    });
    return response.data;
  }

  // Проверить доступность username
  static async checkUsername(
    username: string,
  ): Promise<{ available: boolean; message?: string }> {
    const response = await api.get("/auth/check-username", {
      params: { username },
    });
    return response.data;
  }

  // Удаление пользователя
  static async deleteUser(): Promise<void> {
    try {
      await api.delete("/me");
    } catch (error) {
      handleError(error, { component: "AuthService", action: "deleteUser" });
      throw error;
    }
  }

  // Восстановление пользователя
  static async restoreUser(): Promise<void> {
    try {
      await api.post("/me/restore");
    } catch (error) {
      handleError(error, { component: "AuthService", action: "restoreUser" });
      throw error;
    }
  }
}
